import * as React from 'react';
import { throttle } from '@/src/utils';
import { BasicNutritionAllergenTypeEnum, GoodDetailSkuExtraInfo } from '@/src/pages/shop/c_shop_golden/component';
import { PositionSize, NutritionAllergenInfoItem } from '../types';

import './index.css';

// 记录当前页面常量信息
const ScreenWidth = Mach.env.screenWidth;
const isAndroid = Mach.env.isAndroid;
const ScreenHeight = Mach.env.screenHeight;
const PagePadding = 16;
const MaxContentWidth = ScreenWidth - PagePadding * 2; // 内容面板的最大宽度

interface Props {
  currentSpreadType: BasicNutritionAllergenTypeEnum; // 当前展开的类型
  extraInfo: GoodDetailSkuExtraInfo;
  onModalSpreadChange: Function; // 处理展开/收起modal
  groupSkuId?: number; // (选项组使用)选项组展示时，传入选项组id 用于区分哪个item展示modal
  iconCenterX: Record<BasicNutritionAllergenTypeEnum, number>;
  iconCenterY: number;
}

interface State {
  contentRef: React.RefObject<any>;
  isVisible: boolean; // 是否展示 默认不展示，待布局确定之后再展示
  translateX: number; // 内容面板的translateX
  translateY: number; // 内容面板的translateY
  contentLength: number; // 内容实际高度
  indicator: {
    visible: boolean;
    height: number;
    translateY: number;
  };
  androidContentTop: number; // 当前内容面板的top值(由于安卓同时使用top和translateY会导致面板展示位置不准确，故使用top)
}

export default class InfoModal extends React.Component<Props, State> {
  scrollerRef: any = React.createRef(); // 内容列表引用
  isContentBelowIcon: boolean = true; // 内容面板是否在icon的下方 默认为true

  constructor(props: any) {
    super(props);
    this.state = {
      contentRef: React.createRef(),
      isVisible: false,
      translateX: 0,
      translateY: 0,
      contentLength: 0,
      indicator: {
        visible: false,
        height: 0,
        translateY: 0,
      },
      androidContentTop: 0,
    };
  }

  componentDidMount() {
  }

  render() {
    const {
      extraInfo, onModalSpreadChange, groupSkuId, iconCenterX, iconCenterY, currentSpreadType
    } = this.props;
    const {
      contentRef, isVisible, translateX, translateY, indicator, androidContentTop
    } = this.state;

    let infoList: { name: string; value?: string; }[] = [];

    const infoMapping = {
      [BasicNutritionAllergenTypeEnum.Nutrition]: extraInfo?.nutritionInfo?.valueList,
      [BasicNutritionAllergenTypeEnum.Allergen]: extraInfo?.allergiesInfo?.valueList,
    };

    infoList = infoMapping[currentSpreadType] || [];

    this.isContentBelowIcon = iconCenterY <= ScreenHeight / 2; // 内容面板是否在icon的下方 默认为true

    // 箭头位置
    const arrowLeftX = iconCenterX[currentSpreadType] - 10; // 10为箭头的宽度

    const arrowOffset = this.isContentBelowIcon ? 11.5 : -18.5; // 6为icon高度的一半 5.5为margin 7为箭头的高度
    const arrowTopY = iconCenterY + arrowOffset; // 根据 this.isContentBelowIcon 调整偏移量

    // 内容面板位置
    const contentOffset = this.isContentBelowIcon ? 7 : 0; // 7为箭头的高度
    const contentTopY = arrowTopY + contentOffset; // 根据 this.isContentBelowIcon 调整偏移量

    const arrowImgSrc = this.isContentBelowIcon ? 'assets://shop/up_arrow_img.png' : 'assets://shop/down_arrow_img.png';

    return (
      <modal
        className="modal"
        style={{
          opacity: isVisible ? 1 : 0,
        }}
        onClick={() => {
          onModalSpreadChange({ isSpread: false, groupSkuId });
        }}
        onDragStart={() => {
          onModalSpreadChange({ isSpread: false, groupSkuId });
        }}
      >
        {/* 面板内容 营养成分&过敏源 */}
        <view
          className="content-container"
          ref={contentRef}
          style={{
            maxWidth: MaxContentWidth,
            top: isAndroid ? androidContentTop : contentTopY,
            transform: `translateX(${translateX}), translateY(${translateY})`,
          }}
          onLayout={(frame) => {
            if (isVisible) return;
            this.updateContentPosition(frame.detail, contentTopY);
          }}
        >
          <view
            style={{
              borderRadius: 8,
              backgroundColor: '#ffffff',
            }}
          >
            <scroller
              ref={this.scrollerRef}
              className="scroller"
              showScrollIndicator={false}
              bounces
              onScroll={(offset) => {
                this.updateIndicatorLayout(offset.y);
              }}
            >
              <content
                className="scroller-content"
                onLayout={(frame) => {
                  const contentLength = frame.detail.height;
                  this.setState({ contentLength });
                  setTimeout(() => {
                    this.updateIndicatorLayout(0);
                  }, 0);
                }}
              >
                <view
                  className="content-wrapper"
                >
                  {infoList.map((infoItem: NutritionAllergenInfoItem, index) => {
                    const isLastItem = index === infoList.length - 1;
                    const name = infoItem.name;
                    const value = infoItem.value;
                    return (
                      <view
                        key={name}
                        className="item-wrapper"
                        style={{ marginBottom: isLastItem ? 0 : '8' }}
                      >
                        <view
                          className="item-name-wrapper"
                          style={{ paddingRight: value ? 8 : 0 }}
                        >
                          <text className="item-name" content={name} />
                        </view>
                        {value && (
                          <view className="item-value-wrapper">
                            <text className="item-value" content={value} />
                          </view>
                        )}
                      </view>
                    );
                  })}
                </view>
              </content>
            </scroller>
            {/* 自定义滚动条 以改变样式并常驻展示 */}
            {indicator.visible ? <view className="scroller-indicator" style={{ height: indicator.height, transform: `translateY(${indicator.translateY})` }} /> : null}
          </view>
        </view>

        {/* 面板箭头 */}
        <view
          className="arrow-img-wrapper"
          style={{
            left: arrowLeftX,
            top: arrowTopY,
          }}
        >
          <image className="arrow-img" src={arrowImgSrc} />
        </view>
      </modal>
    );
  }

  /* 根据内容面板的宽度 确定其展示位置 */
  updateContentPosition = (position: PositionSize, contentTopY: number) => {
    const { iconCenterX, currentSpreadType } = this.props;

    const curCenterX = iconCenterX[currentSpreadType];
    // 获取 icon 的左侧和右侧距离
    const minLeftDistance = curCenterX - PagePadding;
    const minRightDistance = ScreenWidth - curCenterX - PagePadding;
    const iconNearLeft = minLeftDistance < minRightDistance; // icon是否靠近屏幕左侧

    const halfContentWidth = position.width / 2; // 内容面板宽度的一半
    const contentCenterX = position.x + halfContentWidth; // 当前面板的中心点位置

    let translateX;

    // 计算 translateX
    if (iconNearLeft) { // icon靠近屏幕左侧
      translateX = (halfContentWidth < minLeftDistance)
        ? curCenterX - contentCenterX // case1: 内容面板可以完全展示在icon的左侧
        : PagePadding - position.x; // case2: 内容面板无法完全展示在icon的左侧
    } else { // icon靠近屏幕右侧
      const contentRightX = position.x + position.width;
      translateX = (halfContentWidth < minRightDistance)
        ? curCenterX - contentCenterX // case3: 内容面板可以完全展示在icon的右侧
        : ScreenWidth - PagePadding - contentRightX; // case4: 内容面板无法完全展示在icon的右侧
    }

    // 计算y轴位置
    let translateY = 0;
    let androidContentTop = contentTopY;

    if (!this.isContentBelowIcon) {
      translateY = -position.height; // 内容面板在icon的上方
      if (isAndroid) {
        androidContentTop -= position.height;
      }
    }
    isAndroid && this.setState({ androidContentTop });

    this.setState({
      translateX,
      translateY,
    });

    this.setState({ isVisible: true });
  }

  /* 初始化滚动条位置
  * @param {number} contentLength 内容实际高度
   */
  updateIndicatorLayout = throttle((scrollTop: number) => {
    const { contentLength } = this.state;
    const scrollFrame = this.scrollerRef?.current?.measure();
    scrollFrame && this.calIndicatorLayout(scrollFrame.height, contentLength, scrollTop);
  }, 1000 / 60);

  /* 计算滚动条位置
  * @param {number} containerHeight 滚动容器高度
  * @param {number} contentHeight 内容高度
  * @param {number} scrollTop 滚动位移
  */
  calIndicatorLayout = throttle((containerHeight, contentHeight, scrollTop) => {
    const padding = 12; // 滚动条上下边距
    const scrollHeight = contentHeight - containerHeight; // 滚动位移范围
    const visible = scrollHeight > 0; // 滚动条是否展示
    const indicatorSlideHeight = containerHeight - padding * 2; // 滚动条滑动范围
    const indicatorHeight = containerHeight / contentHeight * indicatorSlideHeight; // 滚动条高度
    const indicatorTop = Math.max(0, Math.min(scrollTop / scrollHeight, 1))
      * (indicatorSlideHeight - indicatorHeight); // 滚动条滚动位置

    this.setState({
      indicator: {
        visible,
        height: indicatorHeight,
        translateY: indicatorTop + padding
      }
    });
  }, 1000 / 60);
}
